import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Globe, Search, FileText, CheckCircle2, Loader2, AlertCircle, ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "crawling", label: "Crawling Website", icon: Globe, color: "#005EB8" },
  { key: "analyzing", label: "Analyzing Content", icon: Search, color: "#10b981" },
  { key: "generating", label: "Generating Deliverable", icon: FileText, color: "#8b5cf6" },
  { key: "done", label: "Complete", icon: CheckCircle2, color: "#10b981" }
];

export default function JobStatus() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${API}/jobs/${jobId}`);
        setJob(response.data);
        
        if (response.data.status === "completed") {
          setPolling(false);
          toast.success("Analysis complete!");
        }
        if (response.data.status === "error") {
          setPolling(false);
        }
      } catch (error) {
        toast.error("Failed to fetch job status");
        setPolling(false);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();

    if (polling) {
      const interval = setInterval(fetchJob, 3000);
      return () => clearInterval(interval);
    }
  }, [jobId, polling]);

  const getCurrentStepIndex = () => {
    if (!job) return 0;
    const index = STEPS.findIndex(s => s.key === job.current_step);
    return index >= 0 ? index : 0;
  };

  const getProgressPercentage = () => {
    if (!job) return 0;
    if (job.status === "completed") return 100;
    const stepIndex = getCurrentStepIndex();
    return ((stepIndex + 1) / STEPS.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
          <p className="text-[#6b7280]">Loading job status...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#f59e0b] mx-auto mb-4" />
          <p className="text-[#374151] font-semibold mb-4">Job not found</p>
          <Link to="/">
            <Button className="btn-primary">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-brand hover:text-brand-dark transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Home</span>
            </Link>
            <div className="text-sm text-[#6b7280]">
              Job ID: <span className="font-mono">{jobId.slice(0, 8)}...</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Status Header */}
        <div className="text-center mb-8">
          <Badge 
            className={`mb-4 ${job.status === 'completed' ? 'bg-[#10b981] text-white' : job.status === 'error' ? 'bg-[#ef4444] text-white' : 'bg-brand text-white'}`}
            data-testid="job-status-badge"
          >
            {job.status === 'completed' ? 'Completed' : job.status === 'error' ? 'Failed' : 'Processing'}
          </Badge>
          <h1 className="text-2xl md:text-3xl font-semibold text-brand-ink" data-testid="job-title">
            {job.status === 'completed' ? 'Analysis Complete!' : job.status === 'error' ? 'Analysis Failed' : 'Analyzing Your URLs...'}
          </h1>
          <p className="text-[#6b7280] mt-2">
            {job.completed} of {job.total} URLs processed
          </p>
        </div>

        {/* Progress Bar */}
        <Card className="card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-[#374151]">Overall Progress</span>
            <span className="text-sm font-semibold text-brand">{Math.round(getProgressPercentage())}%</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-3" data-testid="progress-bar" />
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Timeline */}
          <div className="lg:col-span-1">
            <Card className="card p-6">
              <h2 className="text-xs font-semibold text-brand uppercase tracking-[0.1em] mb-6">Progress Timeline</h2>
              <ol className="relative border-l border-[#e2e8f0] pl-6" data-testid="job-timeline">
                {STEPS.map((step, index) => {
                  const StepIcon = step.icon;
                  const currentIndex = getCurrentStepIndex();
                  const isActive = index === currentIndex && job.status !== 'completed';
                  const isComplete = index < currentIndex || job.status === 'completed';
                  
                  return (
                    <li key={step.key} className="mb-8 last:mb-0">
                      <span className="absolute -left-2 top-0" aria-hidden>
                        <span 
                          className={`w-4 h-4 rounded-full block ${isActive ? 'timeline-active' : ''}`}
                          style={{ 
                            background: isComplete || isActive ? step.color : '#e2e8f0',
                            boxShadow: isActive ? `0 0 0 4px ${step.color}20` : 'none'
                          }} 
                        />
                      </span>
                      <div className="flex items-center gap-3">
                        <StepIcon 
                          size={18} 
                          className={isActive ? 'animate-pulse' : ''}
                          style={{ color: isComplete || isActive ? step.color : '#9ca3af' }} 
                        />
                        <span className={`text-sm font-medium ${isComplete || isActive ? 'text-[#374151]' : 'text-[#9ca3af]'}`}>
                          {step.label}
                        </span>
                        {isActive && (
                          <Badge variant="secondary" className="text-xs" data-testid="timeline-active-badge">
                            In Progress
                          </Badge>
                        )}
                        {isComplete && index < STEPS.length - 1 && (
                          <CheckCircle2 size={14} className="text-[#10b981]" />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </Card>
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            <Card className="card p-6">
              <h2 className="text-xs font-semibold text-brand uppercase tracking-[0.1em] mb-6">Analysis Results</h2>
              
              {job.results.length === 0 ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-brand animate-spin mx-auto mb-4" />
                  <p className="text-[#6b7280]">Waiting for results...</p>
                  {job.current_url && (
                    <p className="text-sm text-[#9ca3af] mt-2">Currently processing: {job.current_url}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {job.results.map((result, index) => (
                    <div key={index} className="border border-[#e2e8f0] rounded-lg p-4" data-testid={`result-item-${index}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {result.status === 'completed' ? (
                              <CheckCircle2 className="w-5 h-5 text-[#10b981] flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-[#f59e0b] flex-shrink-0" />
                            )}
                            <span className="font-medium text-[#374151] truncate">
                              {result.company_name || result.url}
                            </span>
                          </div>
                          <p className="text-sm text-[#6b7280] truncate pl-7">{result.url}</p>
                        </div>
                        
                        {result.status === 'completed' && (
                          <div className="flex items-center gap-4 ml-4">
                            <div className="text-right">
                              <div className="text-2xl font-semibold text-brand-ink" data-testid={`score-${index}`}>
                                {result.final_score}
                              </div>
                              <div className="text-xs text-[#6b7280]">/100</div>
                            </div>
                            {result.deliverable_id ? (
                              <Button
                                onClick={() => navigate(`/results/${result.deliverable_id}`)}
                                className="btn-primary"
                                data-testid={`view-results-${index}`}
                              >
                                View
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            ) : result.public_score_id ? (
                              <Button
                                onClick={() => navigate(`/public-scores/${result.public_score_id}`)}
                                className="btn-primary"
                                data-testid={`view-public-score-${index}`}
                              >
                                View Score
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            ) : (
                              <Button
                                disabled
                                className="btn-secondary opacity-50 cursor-not-allowed"
                                title="Deliverable not available"
                              >
                                Unavailable
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {result.status === 'error' && (
                          <Badge variant="destructive" className="ml-4">Error</Badge>
                        )}
                      </div>
                      
                      {result.error && (
                        <p className="text-sm text-red-600 mt-2 pl-7">{result.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Quick Actions */}
            {job.status === 'completed' && job.results.some(r => r.status === 'completed') && (
              <Card className="card p-6 mt-6">
                <h3 className="text-xs font-semibold text-brand uppercase tracking-[0.1em] mb-4">Quick Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <Link to="/" data-testid="new-analysis-link">
                    <Button className="btn-secondary">New Analysis</Button>
                  </Link>
                  <Link to="/history" data-testid="view-history-link">
                    <Button className="btn-secondary">View History</Button>
                  </Link>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
